import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCompanies, addExpensesInvoice, updateExpensesInvoice, getLatestCompanyInvoice, getExpensesInvoiceByNumber, getLatestExpensesInvoiceNumber } from '../../firebase/database';  // Updated import to use getInvoices for revenue invoices
import { generateExpensesInvoicePDF, openPDFInNewTab } from '../../services/invoicePdfService';
import { LogisticsInvoice, InvoiceItem, Company, ExpensesInvoice } from '../../types';
import { roundMoney, mulMoney, addMoney, roundTo } from '../../utils/number';
import toast, { Toaster } from 'react-hot-toast';
import SuccessDialog from '../../components/SuccessDialog';
import LoadingOverlay from '../LoadingOverlay';

// Item description suggestions
const ITEM_DESCRIPTION_SUGGESTIONS = [
  // Air Freight
  'AIR FREIGHT',
  'ADVANCE INSURANCE SURCHARGES',
  'AIRWAY BILL FEE',
  'CGC CHARGES',
  'CUSTOM CLEARANCE',
  
  // Ocean Freight
  'OCEAN FREIGHT',
  'BL FEE',
  'LOLO CHARGES',
  'SEAL AND CRO CHARGES',
  'HLA LIVE TRACKING CHARGES',
  'INSURANCE CHARGES',
  'TELEX CHARGES',
  'LOCAL TRANSPORTATION',
  'TRUCK DETAIN',
  'WHARFAGE CHARGES',
  'PLUGIN CHARGES',
  
  // General Charges
  'EFILING TOKEN',
  'PAYMENT AGAINST PSW GD FEE',
  'CUSTOM DUTY',
  'SALES TAX',
  'ADDITIONAL CUSTOM DUTY',
  'ADDITIONAL SALES TAX',
  'INCOME TAX',
  'PRA CHARGES',
  'STORAGE CHARGES',
  'CIVIL AVIATION CHARGES',
  'DELIVERY ORDER CHARGES',
  'APPRAISEMENT CHARGES',
  'EXAMINATION CHARGES',
  'DELIVERY AND MISL. CHARGES',
  'TRANSPORTATION CHARGES',
  'INVOICE NOT FOUND CHARGES',
  'EXTRA CUSTOM CHARGES',
  'AGENCY CHARGES'
];

// Autocomplete component for item descriptions
interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  required,
  inputRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input value
  useEffect(() => {
    if (value.length > 0) {
      const filtered = ITEM_DESCRIPTION_SUGGESTIONS.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setFilteredSuggestions(ITEM_DESCRIPTION_SUGGESTIONS);
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [value]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setFilteredSuggestions(ITEM_DESCRIPTION_SUGGESTIONS);
    setIsOpen(true);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          handleSuggestionClick(filteredSuggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        required={required}
        autoComplete="off"
      />
      
      {isOpen && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-3 py-2 cursor-pointer text-sm ${
                index === highlightedIndex
                  ? 'bg-indigo-50 text-indigo-900'
                  : 'text-gray-900 hover:bg-gray-50'
              }`}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateExpensesInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const logisticsCompanies = {
    'VECTOR LOGISTICS': {
      phone: 'Tel 042-35944728, 3588129',
      address: 'Office M-7, M Floor, Khan Arcade, Firdous Market, Gulberg-III, Lahore',
      logo: '/assets/vector_logo.png'
    },
    'BARQ CARGO SYSTEM PVT LTD.': {
      phone: 'Tel 042-35944728, 3588129',
      address: 'Office M-7, M Floor, Khan Arcade, Firdous Market, Gulberg-III, Lahore',
      logo: '/assets/barq_logo-min.png'
    }
  };
  
  // Refs for input fields
  const selectedCompanyRef = useRef<HTMLSelectElement>(null);
  const invoiceNumberRef = useRef<HTMLInputElement>(null);
  const sailingDateRef = useRef<HTMLInputElement>(null);
  const portOfDestinationRef = useRef<HTMLInputElement>(null);
  const blAwbNumberRef = useRef<HTMLInputElement>(null);
  const itemDescriptionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemQuantityRefs = useRef<(HTMLInputElement | null)[]>([]);
  const itemRateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const currencyRef = useRef<HTMLSelectElement>(null);
  const exchangeRateRef = useRef<HTMLInputElement>(null);
  
  // Company selection
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [companyOutstanding, setCompanyOutstanding] = useState<number>(0);
  
  
  // Logistics company selection
  const [logisticsCompany, setLogisticsCompany] = useState<'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.'>('VECTOR LOGISTICS');
  
  // Invoice details
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [sailingDate, setSailingDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // Shipment details
  const [portOfDestination, setPortOfDestination] = useState<string>('');
  const [blAwbNumber, setBlAwbNumber] = useState<string>('');
  
  // Financial details
  const [items, setItems] = useState<InvoiceItem[]>([
    {
      id: '1',
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: 'PKR'
    }
  ]);
  const [currency, setCurrency] = useState<string>('PKR');
  const [exchangeRate, setExchangeRate] = useState<number>(0);
  const [invoiceDescription, setInvoiceDescription] = useState<string>('');


  // UI states
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [isSavingDraft, setIsSavingDraft] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Draft invoice tracking - to update same draft instead of creating new ones
  const [draftInvoiceNumber, setDraftInvoiceNumber] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...');

  // Load companies on component mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companiesData = await getCompanies();
        setCompanies(companiesData);
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    loadCompanies();
  }, []);

  // Update company outstanding when company changes
  useEffect(() => {
    if (selectedCompany) {
      const company = companies.find(c => c.id === selectedCompany);
      setCompanyOutstanding(company?.outstanding || 0);
    }
  }, [selectedCompany, companies]);

  // Auto-populate fields with latest company invoice data when company is selected
  useEffect(() => {
    const populateFromLatestInvoice = async () => {
      if (selectedCompany) {
        try {
          const latestInvoice = await getLatestCompanyInvoice(selectedCompany);
          if (latestInvoice) {
            // Populate logistics company
            if (latestInvoice.logisticsCompany) {
              setLogisticsCompany(latestInvoice.logisticsCompany);
            }
            
            // Populate invoice number
            if (latestInvoice.invoiceNumber) {
              setInvoiceNumber(latestInvoice.invoiceNumber);
            }
            
            // Populate shipment details
            if (latestInvoice.awbNumber || latestInvoice.blNumber) {
              setBlAwbNumber(latestInvoice.awbNumber || latestInvoice.blNumber || '');
            }
            if (latestInvoice.portOfDestination) {
              setPortOfDestination(latestInvoice.portOfDestination);
            }
            
            // Populate financial details
            if (latestInvoice.currency) setCurrency(latestInvoice.currency);
          if (typeof latestInvoice.exchangeRate === 'number') setExchangeRate(latestInvoice.exchangeRate);
            if (latestInvoice.invoiceDescription) setInvoiceDescription(latestInvoice.invoiceDescription);
            
            // Populate items (use all items from latest invoice)
            if (latestInvoice.items && latestInvoice.items.length > 0) {
              setItems(latestInvoice.items.map((item, index) => ({
                id: (index + 1).toString(),
                description: item.description || '',
                quantity: item.quantity || 1,
                rate: item.rate || 0,
                amount: item.amount || 0,
                currency: item.currency || 'PKR'
              })));
            }
            
            toast.success('Fields populated from last invoice', { position: 'bottom-right' });
          }
        } catch (error) {
          console.error('Error fetching latest invoice:', error);
        }
      }
    };
    
    populateFromLatestInvoice();
  }, [selectedCompany]);


  const formatExpensesSequence = (sequence: number): string => {
    return sequence.toString().padStart(5, '0');
  };

  const extractExpensesSequence = (invoiceNumber: string, year: number): number | null => {
    // Try format: YYYY-MM-NNNNN or YYYY-MM-NNNN
    let match = invoiceNumber.match(/^(\d{4})-(\d{2})-(\d+)$/);
    if (match) {
      const invoiceYear = parseInt(match[1], 10);
      const sequence = parseInt(match[3], 10);
      // Only check year, sequence continues across months
      if (invoiceYear === year) {
        return sequence;
      }
      return null;
    }
    
    // Try old format without month: YYYY-NNNNN
    match = invoiceNumber.match(/^(\d{4})-(\d+)$/);
    if (match) {
      const invoiceYear = parseInt(match[1], 10);
      const sequence = parseInt(match[2], 10);
      if (invoiceYear === year) {
        return sequence;
      }
      return null;
    }
    
    return null;
  };

  // Generate invoice number with sequential unique IDs
  const generateInvoiceNumber = async (): Promise<string> => {
    const date = sailingDate ? new Date(sailingDate) : new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const prefix = `${year}-${month}-`;

    try {
      const latestInvoiceNumber = await getLatestExpensesInvoiceNumber();
      let sequence = 1;

      if (latestInvoiceNumber) {
        // Extract sequence based on year only (continues across months)
        const latestSequence = extractExpensesSequence(latestInvoiceNumber, year);
        if (latestSequence !== null) {
          sequence = latestSequence + 1;
        }
      }

      const maxAttempts = 100;
      let attempts = 0;

      while (attempts < maxAttempts) {
        const candidate = `${prefix}${formatExpensesSequence(sequence)}`;
        const existingInvoice = await getExpensesInvoiceByNumber(candidate);
        if (!existingInvoice) {
          return candidate;
        }
        sequence += 1;
        attempts += 1;
      }

      throw new Error('Unable to generate unique expenses invoice number');
    } catch (error) {
      console.warn('Error generating expenses invoice number:', error);
      const fallback = Date.now().toString().slice(-5).padStart(5, '0');
      return `${prefix}${fallback}`;
    }
  };

  // Add new item
  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      currency: currency
    };
    setItems([...items, newItem]);
  };

  // Remove item
  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  // Update item
  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate amount for quantity, rate, or currency changes
        if (field === 'quantity' || field === 'rate' || field === 'currency') {
          updatedItem.amount = mulMoney(updatedItem.quantity, updatedItem.rate);
        }
        return updatedItem;
      }
      return item;
    }));
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return roundMoney(items.reduce((sum, item) => addMoney(sum, item.amount), 0));
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  // Calculate PKR totals
  const calculateSubtotalPKR = () => {
    const total = items.reduce((sum, item) => {
      if (item.currency !== 'PKR') {
        return addMoney(sum, mulMoney(item.amount, exchangeRate));
      }
      return addMoney(sum, item.amount);
    }, 0);
    return roundMoney(total);
  };

  const calculateTotalPKR = () => {
    return calculateSubtotalPKR();
  };

  const generateRandomPrice = () => {
    // Limit auto-populated item rate to 1 or 2 (currency-aware elsewhere)
    return Math.random() < 0.5 ? 1 : 2;
  };

  const populateForm = async () => {
    setSelectedCompany(companies[0]?.id || ''); // Use first company if available
    const invoiceNumber = await generateInvoiceNumber();
    setInvoiceNumber(invoiceNumber);
    setSailingDate(new Date().toISOString().split('T')[0]);
    setPortOfDestination('Jinnah International Airport');
    setBlAwbNumber(`AWB${Date.now()}`);
    
    const item1Rate = generateRandomPrice();
    const item2Rate = generateRandomPrice();
    
    setItems([
      {
        id: '1',
        description: 'Electronics Components',
        quantity: 2,
        rate: item1Rate,
        amount: 2 * item1Rate,
        currency: 'USD'
      },
      {
        id: '2',
        description: 'Customs Clearance Services',
        quantity: 1,
        rate: item2Rate,
        amount: 1 * item2Rate,
        currency: 'PKR'
      }
    ]);
    setCurrency('USD');
    setExchangeRate(280);
  };

  // Preview PDF
  const handlePreviewPDF = async () => {
    setShowValidation(true);
    if (!validateForm()) {
      return;
    }
    setIsGeneratingPDF(true);
    setIsLoading(true);
    setLoadingMessage('Generating PDF preview...');
    try {
      const invoice: ExpensesInvoice = {
        id: 'preview',
        invoiceNumber: invoiceNumber,
        clientId: selectedCompany,
        clientName: companies.find(c => c.id === selectedCompany)?.name || '',
        date: new Date(sailingDate),
        awbNumber: blAwbNumber,
        portOfDestination: portOfDestination,
        blNumber: blAwbNumber,
        items,
        subtotal: calculateSubtotalPKR(),
        total: calculateTotalPKR(),
        currency,
        exchangeRate,
        invoiceDescription: '',
        status: 'pending',
        createdBy: currentUser?.id || '',
        createdAt: new Date(),
        logisticsCompany: logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
      };

      const pdf = await generateExpensesInvoicePDF(invoice);
      openPDFInNewTab(pdf, 'Expenses Invoice PDF');
      toast.success('PDF preview generated successfully!', { position: 'bottom-right' });
    } catch (error) {
      toast.error('Error generating PDF preview', { position: 'bottom-right' });
      console.error('PDF generation error:', error);
    } finally {
      setIsGeneratingPDF(false);
      setIsLoading(false);
    }
  };

  // Scroll to first empty field
  const scrollToFirstEmptyField = (errors: Record<string, string>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Priority order for scrolling to fields
    const fieldPriority = [
      'selectedCompany',
      'invoiceNumber',
      'sailingDate',
      'portOfDestination',
      'blAwbNumber',
      'currency',
      'exchangeRate'
    ];

    // Find the first error field in priority order
    let targetField = null;
    for (const priorityField of fieldPriority) {
      if (errors[priorityField]) {
        targetField = priorityField;
        break;
      }
    }

    // If no priority field found, check for item errors
    if (!targetField) {
      for (const errorKey of errorKeys) {
        if (errorKey.startsWith('item_')) {
          targetField = errorKey;
          break;
        }
      }
    }

    // Scroll to the target field
    if (targetField) {
      let targetRef: React.RefObject<HTMLElement> | null = null;
      
      switch (targetField) {
        case 'selectedCompany':
          targetRef = selectedCompanyRef;
          break;
        case 'invoiceNumber':
          targetRef = invoiceNumberRef;
          break;
        case 'sailingDate':
          targetRef = sailingDateRef;
          break;
        case 'portOfDestination':
          targetRef = portOfDestinationRef;
          break;
        case 'blAwbNumber':
          targetRef = blAwbNumberRef;
          break;
        case 'currency':
          targetRef = currencyRef;
          break;
        case 'exchangeRate':
          targetRef = exchangeRateRef;
          break;
        default:
          // Handle item errors
          if (targetField.startsWith('item_')) {
            const parts = targetField.split('_');
            const index = parseInt(parts[1]);
            const field = parts[2];
            
            if (field === 'description' && itemDescriptionRefs.current[index]) {
              targetRef = { current: itemDescriptionRefs.current[index] };
            } else if (field === 'quantity' && itemQuantityRefs.current[index]) {
              targetRef = { current: itemQuantityRefs.current[index] };
            } else if (field === 'rate' && itemRateRefs.current[index]) {
              targetRef = { current: itemRateRefs.current[index] };
            }
          }
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        targetRef.current.focus();
      }
    }
  };

  // Validate form
  const validateDraftForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Both company and logistics company selection are required for draft
    if (!selectedCompany) {
      errors['selectedCompany'] = 'Required';
    }
    
    if (!logisticsCompany) {
      errors['logisticsCompany'] = 'Required';
    }
    
    if (!selectedCompany || !logisticsCompany) {
      toast.error('Both Company and Logistics Company selection are required to save draft', { position: 'bottom-right' });
    }
    
    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Check required fields and show toast for each missing field
    if (!selectedCompany) {
      errors['selectedCompany'] = 'Required';
      toast.error('Company selection is required', { position: 'bottom-right' });
    }
    if (!invoiceNumber) {
      errors['invoiceNumber'] = 'Required';
      toast.error('Invoice number is required', { position: 'bottom-right' });
    }
    if (!sailingDate) {
      errors['sailingDate'] = 'Required';
      toast.error('Sailing date is required', { position: 'bottom-right' });
    }
    if (!portOfDestination) {
      errors['portOfDestination'] = 'Required';
      toast.error('Port of destination is required', { position: 'bottom-right' });
    }
    if (!blAwbNumber) {
      errors['blAwbNumber'] = 'Required';
      toast.error('BL/AWB number is required', { position: 'bottom-right' });
    }
    if (!currency) {
      errors['currency'] = 'Required';
      toast.error('Currency selection is required', { position: 'bottom-right' });
    }
    if (exchangeRate < 0) {
      errors['exchangeRate'] = 'Exchange rate cannot be negative';
      toast.error('Exchange rate must be greater than 0', { position: 'bottom-right' });
    }
    if (!invoiceDescription.trim()) {
      errors['invoiceDescription'] = 'Required';
      toast.error('Invoice description is required', { position: 'bottom-right' });
    }
    
    items.forEach((item, index) => {
      if (!item.description) {
        errors[`item_${index}_description`] = 'Required';
        toast.error(`Item ${index + 1} description is required`, { position: 'bottom-right' });
      }
      if (item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Must be > 0';
        toast.error(`Item ${index + 1} quantity must be greater than 0`, { position: 'bottom-right' });
      }
      if (item.rate <= 0) {
        errors[`item_${index}_rate`] = 'Must be > 0';
        toast.error(`Item ${index + 1} rate must be greater than 0`, { position: 'bottom-right' });
      }
    });
    
    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  // Submit invoice
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setIsLoading(true);
    setLoadingMessage('Creating expenses invoice...');
    try {
      // Check if there's an existing draft to update
      let finalInvoiceNumber = draftInvoiceNumber || invoiceNumber;
      if (!finalInvoiceNumber) {
        finalInvoiceNumber = await generateInvoiceNumber();
      }
      
      const invoice: Omit<ExpensesInvoice, 'id' | 'createdAt'> = {
        invoiceNumber: finalInvoiceNumber,
        clientId: selectedCompany,
        clientName: companies.find(c => c.id === selectedCompany)?.name || '',
        date: new Date(sailingDate),
        awbNumber: blAwbNumber,
        portOfDestination: portOfDestination,
        blNumber: blAwbNumber,
        items,
        subtotal: calculateSubtotalPKR(),
        total: calculateTotalPKR(),
        currency,
        exchangeRate,
        invoiceDescription: invoiceDescription,
        status: 'pending',
        createdBy: currentUser?.id || '',
        logisticsCompany: logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
      };

      // Remove undefined fields before sending to Firebase
      const cleanInvoice = Object.fromEntries(
        Object.entries(invoice).filter(([_, value]) => value !== undefined)
      );

      // If there's an existing draft, update it; otherwise create a new invoice
      if (draftInvoiceNumber) {
        await updateExpensesInvoice(finalInvoiceNumber, cleanInvoice as Partial<Omit<ExpensesInvoice, 'id' | 'createdAt' | 'approvedAt'>>);
        toast.success('Draft converted to invoice successfully!', { position: 'bottom-right' });
      } else {
        await addExpensesInvoice(cleanInvoice as Omit<LogisticsInvoice, 'id' | 'createdAt'>);
        toast.success('Expenses invoice created successfully!', { position: 'bottom-right' });
      }
      setShowSuccessDialog(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(errorMessage, { position: 'bottom-right' });
      console.error('Expenses invoice creation error:', error);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Save as draft
  const handleSaveDraft = async () => {
    setShowValidation(true);
    if (!validateDraftForm()) {
      return;
    }
    setIsSavingDraft(true);
    setIsLoading(true);
    setLoadingMessage('Saving draft...');
    try {
      // Reuse existing draft invoice number or generate new one
      let finalInvoiceNumber = draftInvoiceNumber || invoiceNumber;
      if (!finalInvoiceNumber) {
        finalInvoiceNumber = await generateInvoiceNumber();
        setDraftInvoiceNumber(finalInvoiceNumber); // Store for subsequent saves
      }
      
      const invoice: Omit<ExpensesInvoice, 'id' | 'createdAt'> = {
        invoiceNumber: finalInvoiceNumber,
        clientId: selectedCompany,
        clientName: companies.find(c => c.id === selectedCompany)?.name || '',
        date: new Date(sailingDate || new Date().toISOString().split('T')[0]),
        awbNumber: blAwbNumber || '',
        portOfDestination: portOfDestination || '',
        blNumber: blAwbNumber || '',
        items: items.length > 0 ? items : [], // Allow empty items for draft
        subtotal: calculateSubtotalPKR(),
        total: calculateTotalPKR(),
        currency: currency || 'PKR',
        exchangeRate: typeof exchangeRate === 'number' ? exchangeRate : 0,
        invoiceDescription: invoiceDescription || '',
        status: 'draft',
        createdBy: currentUser?.id || '',
        logisticsCompany: logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.',
        logoUrl: logisticsCompanies[logisticsCompany as keyof typeof logisticsCompanies]?.logo,
      };

      // Remove undefined fields before sending to Firebase
      const cleanInvoice = Object.fromEntries(
        Object.entries(invoice).filter(([_, value]) => value !== undefined)
      );

      // Check if this is an update to existing draft or a new draft
      if (draftInvoiceNumber) {
        // Update existing draft
        await updateExpensesInvoice(finalInvoiceNumber, cleanInvoice as Partial<Omit<ExpensesInvoice, 'id' | 'createdAt' | 'approvedAt'>>);
        toast.success('Draft updated successfully!', { position: 'bottom-right' });
      } else {
        // Create new draft
        await addExpensesInvoice(cleanInvoice as Omit<LogisticsInvoice, 'id' | 'createdAt'>);
        toast.success('Draft saved successfully!', { position: 'bottom-right' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(errorMessage, { position: 'bottom-right' });
      console.error('Draft save error:', error);
    } finally {
      setIsSavingDraft(false);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster />
      <LoadingOverlay isVisible={isLoading} message={loadingMessage} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Expenses Invoice</h1>
              <p className="mt-2 text-gray-600">Create a new expenses invoice</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Development Populate Button */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Development Tools</h3>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={populateForm}
                className="px-4 py-2 text-sm font-medium text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200"
              >
                Auto-Populate
              </button>
            </div>
          </div>

          {/* Company Selection */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Company Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Company *
                </label>
                <select
                  ref={selectedCompanyRef}
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Choose a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} (Outstanding: PKR {company.outstanding.toLocaleString()})
                    </option>
                  ))}
                </select>
                {showValidation && fieldErrors['selectedCompany'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['selectedCompany']}</p>
                )}
              </div>
              {selectedCompany && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600">
                    <strong>Outstanding Balance:</strong> PKR {companyOutstanding.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Shipment Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Shipment Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port of Destination *
                </label>
                <input
                  ref={portOfDestinationRef}
                  type="text"
                  value={portOfDestination}
                  onChange={(e) => setPortOfDestination(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {showValidation && fieldErrors['portOfDestination'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['portOfDestination']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  BL/AWB Number *
                </label>
                <input
                  ref={blAwbNumberRef}
                  type="text"
                  value={blAwbNumber}
                  onChange={(e) => setBlAwbNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {showValidation && fieldErrors['blAwbNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['blAwbNumber']}</p>
                )}
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Number *
                </label>
                <input
                  ref={invoiceNumberRef}
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter invoice number"
                  required
                />
                {showValidation && fieldErrors['invoiceNumber'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['invoiceNumber']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sailing Date *
                </label>
                <input
                  ref={sailingDateRef}
                  type="date"
                  value={sailingDate}
                  onChange={(e) => setSailingDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {showValidation && fieldErrors['sailingDate'] && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors['sailingDate']}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  ref={currencyRef}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="PKR">PKR</option>
                  <option value="USD">USD</option>
                  <option value="AED">AED</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rate of Exchange
                </label>
                <input
                  ref={exchangeRateRef}
                  type="number"
                  value={exchangeRate}
                  onChange={(e) => {
                    const parsed = parseFloat(e.target.value);
                    const nextValue = Number.isNaN(parsed) ? 0 : parsed;
                    setExchangeRate(roundTo(nextValue, 4));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            
            {/* Invoice Description */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Description *
              </label>
              <textarea
                value={invoiceDescription}
                onChange={(e) => setInvoiceDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter invoice description..."
                rows={3}
                required
              />
              {showValidation && !invoiceDescription.trim() && (
                <p className="mt-1 text-sm text-red-600">Invoice description is required</p>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="mb-4">
              <h2 className="text-lg font-medium text-gray-900">Invoice Items</h2>
            </div>
            
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Item {index + 1}</h4>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Item Description *</label>
                      <AutocompleteInput
                        inputRef={{ current: itemDescriptionRefs.current[index] }}
                        value={item.description}
                        onChange={(value) => updateItem(item.id, 'description', value)}
                        placeholder="Enter or select item description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        required
                      />
                      {showValidation && fieldErrors[`item_${index}_description`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`item_${index}_description`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Quantity *</label>
                      <input
                        ref={(el) => (itemQuantityRefs.current[index] = el)}
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="1"
                        min="1"
                        required
                      />
                      {showValidation && fieldErrors[`item_${index}_quantity`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`item_${index}_quantity`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Rate *</label>
                      <input
                        ref={(el) => (itemRateRefs.current[index] = el)}
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.rate}
                        onChange={(e) => updateItem(item.id, 'rate', roundMoney(parseFloat(e.target.value) || 0))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="0.00"
                        required
                      />
                      {showValidation && fieldErrors[`item_${index}_rate`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`item_${index}_rate`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={item.currency}
                        onChange={(e) => updateItem(item.id, 'currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      >
                        <option value="PKR">PKR</option>
                        <option value="USD">USD</option>
                        <option value="AED">AED</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Price:</span>
                      <div className="text-right">
                        <div className="text-sm font-bold text-gray-900">
                          {item.currency} {roundMoney(item.amount).toFixed(2)}
                        </div>
                        {item.currency !== 'PKR' && (
                          <div className="text-xs text-blue-600">
                            PKR: {roundMoney(mulMoney(item.amount, exchangeRate)).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Item Button */}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={addItem}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
              >
                + Add Item
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal (PKR):</span>
                  <span className="font-medium">PKR {roundMoney(calculateSubtotalPKR()).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total (PKR):</span>
                  <span>PKR {roundMoney(calculateTotalPKR()).toFixed(2)}</span>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
                <p className="text-sm text-gray-600">
                  This expenses invoice will show all amounts converted to PKR using the exchange rate of {exchangeRate}.
                </p>
                {currency !== 'PKR' && (
                  <p className="text-sm text-blue-600 mt-2">
                    Original currency: {currency} {roundMoney(calculateTotal()).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handlePreviewPDF}
                disabled={isGeneratingPDF}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
              >
                {isGeneratingPDF ? 'Generating...' : 'Preview PDF'}
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft}
                className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50"
              >
                {isSavingDraft ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating Invoice...' : 'Create Expenses Invoice'}
            </button>
          </div>
        </form>
      </div>
      
      
      {/* Success Dialog */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => {
          setShowSuccessDialog(false);
          navigate('/all-expenses-invoices');
        }}
        title="Expenses Invoice Created Successfully!"
        message="Your expenses invoice has been created and is now pending approval. You will be redirected to all expenses invoices."
        buttonText="Go to All Expenses Invoices"
      />
    </div>
  );
};

export default CreateExpensesInvoice;
